
// **** code de mattching type *****
import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Spin,
  Divider,
  Alert,
  Radio,
  Checkbox,
  Card,
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
} from "@ant-design/icons";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MatchingQuestionViewer from "./MatchingQuestionViewer";
import courseService from "../../services/course.service";  // ← ajouter l'import

const { Title } = Typography;

const QuizzesSubject = ({ subject, courseId, onSubmitResult }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userAnswers, setUserAnswers] = useState({});
  const [initialAnswersMap, setInitialAnswersMap] = useState({});
  const [allQuestions, setAllQuestions] = useState([]); // ✅ AJOUTER CETTE LIGNE

  const quizzes = subject?.quizzes || [];

  // ✅ Charger les questions à la demande
 useEffect(() => {
  let cancelled = false;

  const loadQuestions = async () => {
    if (!quizzes.length) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        quizzes.map(async (quiz) => {
          if (quiz.questions?.length > 0 && quiz.questions[0].answers?.length > 0) {
            return quiz.questions.map(q => ({ ...q, quizId: quiz.id, quizTitle: quiz.title }));
          } else if (quiz.id) {
            // ✅ UN SEUL APPEL au lieu de N+1
            const questions = await courseService.getQuestionsWithAnswers(quiz.id);
            return questions.map(q => ({
              ...q,
              quizId: quiz.id,
              quizTitle: quiz.title
            }));
          }
          return [];
        })
      );

      if (!cancelled) {
        setAllQuestions(results.flat().filter(q => q.answers && q.answers.length > 0));
      }
    } catch (err) {
      console.error("Erreur chargement questions:", err);
    } finally {
      if (!cancelled) setLoading(false);
    }
  };

  loadQuestions();
  return () => { cancelled = true; };
}, [subject?.id]);

  if (!quizzes.length) return null;

  // const currentQuiz = quizzes[0];
  // const questions = currentQuiz?.questions || [];

  const handleSubmit = () => {
    setSubmitted(true);
    let correctCount = 0;

    allQuestions.forEach((q) => {
      const correctAnswers = q.answers.filter((a) => a.correct).map((a) => a.text.trim());
      const user = userAnswers[q.id];

      let isCorrect = false;

      if (q.type === "DRAG_DROP") {
        const userTexts = Array.isArray(user) ? user.map((u) => u.text.trim()) : [];
        isCorrect = JSON.stringify(userTexts) === JSON.stringify(correctAnswers);
      } else if (q.type === "MATCHING") {
        const pairs = q.answers;
        const leftItems = pairs.filter((_, i) => i % 2 === 0).map(p => p.text.trim());
        const rightItems = pairs.filter((_, i) => i % 2 === 1).map(p => p.text.trim());

        isCorrect = leftItems.every(
          (left, idx) => user?.[left] === rightItems[idx]
        );
      } else if (Array.isArray(user)) {
        isCorrect = JSON.stringify(user.sort()) === JSON.stringify(correctAnswers.sort());
      } else {
        isCorrect = user === correctAnswers[0];
      }

      if (isCorrect) correctCount++;
    });

    const score = correctCount * 10;
    const isPassed = score > 0;

    // if (onSubmitResult) {
    //   onSubmitResult(subject.id, isPassed);
    // }
    if (onSubmitResult) {
      console.log("✅ Résultat soumis pour le sujet ID:", subject.id, "=>", isPassed);
      onSubmitResult(subject.id, isPassed);
    }

  };

  // ✅ renderQuestion avec guard
  const renderQuestion = (q) => {
    if (!q.answers || q.answers.length === 0) return null; // ✅ Protection

    const correctAnswers = q.answers.filter((a) => a.correct).map((a) => a.text.trim());
    const user = userAnswers[q.id];
    const isCorrect = Array.isArray(user)
      ? JSON.stringify(user.sort()) === JSON.stringify(correctAnswers.sort())
      : user === correctAnswers[0];

    return (
      <Card key={q.id} style={{ marginBottom: 16 }}>
        <Title level={5}>
          🧪 <span style={{ color: "#fa8c16" }}>{q.quizTitle}</span><br />
          {q.text}
        </Title>

        {q.type === "QCM" && (
          <Checkbox.Group
            value={userAnswers[q.id] || []}
            onChange={(vals) => setUserAnswers({ ...userAnswers, [q.id]: vals })}
            disabled={submitted}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {q.answers.map((a, i) => (
              <Checkbox key={i} value={a.text}>{a.text}</Checkbox>
            ))}
          </Checkbox.Group>
        )}

        {(q.type === "QCU" || q.type === "VRAI_FAUX") && (
          <Radio.Group
            value={userAnswers[q.id]}
            onChange={(e) => setUserAnswers({ ...userAnswers, [q.id]: e.target.value })}
            disabled={submitted}
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {q.answers.map((a, i) => (
              <Radio key={i} value={a.text}>{a.text}</Radio>
            ))}
          </Radio.Group>
        )}

        {q.type === "MATCHING" && (
          <MatchingQuestionViewer
            key={submitted + JSON.stringify(userAnswers[q.id] || {})}
            pairs={q.answers || []}
            questionId={q.id}
            userAnswers={userAnswers}
            setUserAnswers={setUserAnswers}
            disabled={submitted}
          />
        )}

        {q.type === "DRAG_DROP" && (() => {
          const defaultAnswers = q.answers.map((a, i) => ({ id: `a-${i}-${q.id}`, text: a.text }));
          const items = [...(userAnswers[q.id] || defaultAnswers)];

          return (
            <DragDropContext
              onDragEnd={(result) => {
                if (!result.destination) return;
                const newItems = [...items];
                const [removed] = newItems.splice(result.source.index, 1);
                newItems.splice(result.destination.index, 0, removed);
                setUserAnswers({ ...userAnswers, [q.id]: newItems });
              }}
            >
              <Droppable droppableId={`droppable-${q.id}`}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    style={{
                      border: "1px solid #f0f0f0",
                      padding: 12,
                      borderRadius: 6,
                      backgroundColor: "#fafafa",
                      minHeight: 60,
                    }}
                  >
                    {items.map((item, index) => (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              padding: "8px 12px",
                              marginBottom: 8,
                              background: "#fff",
                              border: "1px solid #d9d9d9",
                              borderRadius: 4,
                              ...provided.draggableProps.style,
                            }}
                          >
                            {item.text}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          );
        })()}

        {submitted && (
          <div style={{ marginTop: 12 }}>
            {isCorrect ? (
              <Alert
                type="success"
                message={<span><CheckCircleTwoTone twoToneColor="#52c41a" /> Bonne réponse</span>}
                description={
                  q.explanation?.trim()
                    ? <div dangerouslySetInnerHTML={{ __html: q.explanation }} />
                    : undefined
                }
                showIcon
              />
            ) : (
              <Alert
                type="error"
                message={<span><CloseCircleTwoTone twoToneColor="#ff4d4f" /> Mauvaise réponse</span>}
                description={
                  q.explanation?.trim()
                    ? <div dangerouslySetInnerHTML={{ __html: q.explanation }} />
                    : undefined
                }
                showIcon
              />
            )}
          </div>
        )}
      </Card>
    );
  };


  return (
    <div style={{ marginTop: 24 }}>
      <Divider />
      <Title level={5}>🤔 Quiz - {subject?.title}</Title>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <Spin size="large" tip="Chargement du quiz..." />
        </div>
      ) : (
        <>
          {allQuestions.map(renderQuestion)}

          {!submitted && allQuestions.length > 0 && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <Button type="primary" onClick={handleSubmit}>
                Soumettre le Quiz
              </Button>
            </div>
          )}

          {submitted && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <Button onClick={() => { setSubmitted(false); setUserAnswers({}); }}>
                Recommencer
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuizzesSubject;
